import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQueryParam } from '../shared/hooks/location'
import { useLoad } from '../shared/hooks/load'
import { useApiFormAuthProvider } from '../api-new/state/auth'
import { AuthDivider, AuthHead, AuthLoading, AuthMark, AuthNotice, AuthPage } from '../shared/sso-workspace/auth-card'
import { OrganizationChooser, flaggedFirst, mostRecentKey, orgKey } from '../shared/sso-workspace/organization-chooser'
import { JoinCodeEntry } from '../shared/sso-workspace/join-code-entry'
import { InviteConfirmation } from '../shared/sso-workspace/invite-confirmation'
import { ErrorReference } from '../shared/error/error-reference'
import { SignInLinkSpent, spentReasonOf, useSignInLinkSpent } from '../shared/sso-workspace/sign-in-link-spent'
import { isLinkConfirmationRequired, sendToLinkConfirmation } from '../shared/sso-workspace/link-confirm-api'
import { fromDetails, fromRemovedList, RemovedMembershipNotice, removedSentences } from '../shared/sso-workspace/removed-memberships'

// /open/organization: after a single sign-on, which organization to sign in
// to. The auth service lands the person itself when it can (a remembered
// organization, exactly one company organization) and answers nothing; one
// organization is chosen for the person; two or more are offered as cards
// (OrganizationChooser: when each was last signed in to), the one used last
// first and flagged "Last used".
//
// A rejected sign-in (sso_not_enabled, no_organization) comes back from the
// auth service as a 4xx whose message is the /auth/error URL to show; this
// page navigates there rather than showing the raw error.
//
// Join policy: when the connection placed the person in no organization,
// the list holds the organizations they may choose to join (joinable rows,
// possibly none) and, when the connection has join-code organizations,
// "Have a join code?" (GET /:token/join-code says whether). Picking joins
// only that organization; nothing is picked for them, even when there is
// one.
//
// A spent link (owner report 2026-10-13): the auth service answers
// sign_in_link_used / sign_in_link_expired when the token's sign-in already
// finished -- typically an EducationXR app took it and this is the tab its
// sign-in left behind -- or ran out of time. That is no failure: with a
// session in this browser, straight into the admin; otherwise the calm
// SignInLinkSpent card. Only real failures (5xx, network, anything else)
// show the error with its reference.
//
// Leaving for /open/complete replaces this page in history (never a push),
// so Back or a restored tab does not land on a token already used.
//
// SSO link confirmation (plan 5.2): a sign-in auth holds until the person
// confirms the account is theirs (a public sign-in button met an existing
// account it isn't connected to) answers every step here with 403
// link_confirmation_required; the page for that is /open/link with the same
// token, which this one is replaced by.

// The organization chosen last time on this browser. Only a convenience:
// storage can be unavailable (private windows, blocked site data), so every
// read and write is guarded and the page works the same without it -- the
// most recently signed-in organization (lastSignedInAt) stands in for it.
const LAST_KEY = 'edxr.sso.lastOrganizationID'

const readLast = () => {
  try {
    return window.localStorage.getItem(LAST_KEY)
  } catch {
    return null
  }
}

const writeLast = id => {
  try {
    window.localStorage.setItem(LAST_KEY, String(id))
  } catch {}
}

// The "Last used" organization: this browser's last pick when it is on
// offer, else the one signed in to most recently.
const lastUsedKey = organizations => {
  const stored = readLast()
  return organizations.some(o => orgKey(o) === stored) ? stored : mostRecentKey(organizations)
}

// The server's own words, when it said something more useful than core's
// catch-all "an error occured" (sic) or the service layer's "An error occurred.".
const messageOf = error => {
  const message = (error && ((error.data && error.data.message) || error.message)) || null
  return message && !/^an? error occu?rr?ed\.?$/i.test(message.trim()) ? message : null
}

// A pick auth refused because the person's membership there was removed
// (spec 2026-10-23 SCIM R16/R25: SSO sign-in never brings it back): auth's
// reason or code membership_removed, with the organizations it names
// (removed: [{ organizationName, via }], core's RemovedMembership). The
// sentences are /auth/error's (removed-memberships.js); naming none, they
// name the organization picked (fallbackName). Anything else: null.
export const removedMembershipText = (error, fallbackName) => {
  const body = error && error.data && typeof error.data === 'object' ? error.data : null
  if (!body || (body.reason !== 'membership_removed' && body.code !== 'membership_removed')) return null
  const entries = fromRemovedList(body.removed).filter(e => e.name)
  return removedSentences(entries.length ? entries : [{ name: fallbackName || '', byIdentityProvider: false }]).join(' ')
}

export const OpenOrganization = () => {
  const apiFormAuthProvider = useApiFormAuthProvider()

  const token = useQueryParam('token')

  // loading -> choose | selecting -> (leaves) ; failed on a load error
  const [state, updateState] = useState({ step: 'loading', organizations: [], chosen: null, error: null })

  const [spent, onSpent] = useSignInLinkSpent()

  const complete = () => {
    let url = new URL(`${process.env.REACT_APP_SITE}/open/complete`)
    url.searchParams.set('token', token)
    window.location.replace(url.href)
  }

  const handleSelect = async (organization, organizations) => {
    updateState(prev => ({ ...prev, step: 'selecting', back: prev.step === 'join' ? 'join' : 'choose', organizations: organizations || prev.organizations, chosen: organization, error: null, failure: null, removed: null }))

    try {
      await apiFormAuthProvider.postArg(`/${token}/organization`, organization)
      writeLast(orgKey(organization))
      complete()
    } catch (error) {
      if (onSpent(error)) return
      if (sendToLinkConfirmation(error, token)) return
      // failure: the error behind it, for its reference (error-reference.js);
      // removed: the person's membership there was removed, said in words.
      updateState(prev => ({ ...prev, step: prev.back || 'choose', error: messageOf(error) || 'unknown', failure: error, removed: removedMembershipText(error, organization && organization.name) }))
    }
  }

  // A join code; its refusal is shown by JoinCodeEntry itself.
  const handleJoinCode = async code => {
    try {
      await apiFormAuthProvider.postArg(`/${token}/join-code`, { joinCode: code })
    } catch (error) {
      if (onSpent(error)) return
      if (sendToLinkConfirmation(error, token)) return
      throw error
    }
    complete()
  }

  const codeAvailable = async () => {
    try {
      const status = await apiFormAuthProvider.get(`${token}/join-code`)
      return Boolean(status && status.available)
    } catch {
      return false
    }
  }

  // Invite confirmation (owner ruling 2026-09-27): the account's pending
  // invites come first. Anything but a list of invites (an older auth
  // service, a failure) means none: the sign-in carries on. A spent link is
  // passed on: there is no sign-in to carry on.
  const loadInvites = async () => {
    try {
      const invites = await apiFormAuthProvider.get(`${token}/invites`)
      return Array.isArray(invites) ? invites.filter(i => i && i.userID) : []
    } catch (error) {
      if (spentReasonOf(error) || isLinkConfirmationRequired(error)) throw error
      return []
    }
  }

  const handleDecide = (invite, accept) =>
    apiFormAuthProvider.postArg(`/${token}/invites/${invite.userID}/${accept ? 'accept' : 'decline'}`, {})

  useLoad(async () => {
    let invites

    try {
      invites = await loadInvites()
    } catch (error) {
      if (!onSpent(error)) sendToLinkConfirmation(error, token)
      return
    }

    if (invites.length) {
      updateState({ step: 'invites', invites, organizations: [], chosen: null, error: null })
      return
    }

    await loadOrganizations()
  }, [])

  // R25's one-line notice: the organizations this sign-in did not bring the
  // person back into, while it offers them others (GET /:token/details'
  // removedOrganizations, which the organization step just wrote). Read
  // after the list shows, so it never holds the choice up; any failure says
  // nothing. A person placed straight into one organization sees it on
  // /open/complete instead.
  const loadRemovedNotice = async () => {
    try {
      const entries = fromDetails(await apiFormAuthProvider.get(`${token}/details`))
      if (entries.length) updateState(prev => ({ ...prev, notice: entries }))
    } catch {}
  }

  const loadOrganizations = async () => {

    updateState(prev => ({ ...prev, step: 'loading' }))

    try {
      let organizations = await apiFormAuthProvider.get(`${token}/organization`)

      if (organizations) {

        if (organizations.length === 0 || organizations.some(o => o.joinable)) {
          const code = await codeAvailable()
          updateState({ step: 'join', organizations, chosen: null, error: null, code })
          loadRemovedNotice()
        } else if (organizations.length === 1) {
          handleSelect(organizations[0], organizations)
        } else {
          const last = lastUsedKey(organizations)
          updateState({ step: 'choose', organizations: flaggedFirst(organizations, last), chosen: null, error: null, last })
          loadRemovedNotice()
        }

        return
      } else {
        complete()
      }

    } catch (error) {

      if (onSpent(error)) return

      if (sendToLinkConfirmation(error, token)) return

      // Task 15 carried fix: on a rejected SSO sign-in the auth service
      // composes this failure as `<errorURL>?reason=sso_not_enabled|no_organization&provider=...`
      // in the error body's message (see login-error-copy.js for the two
      // reasons) rather than a plain message meant for this page's own
      // alert. Recognize that shape -- a 4xx whose message is itself a URL
      // -- and navigate there instead of rendering the generic failure.
      const message = error && error.data && error.data.message
      const status = error && error.status

      if (status >= 400 && status < 500 && typeof message === 'string' && /^https?:\/\//i.test(message)) {
        window.location.assign(message)
        return
      }

      updateState({ step: 'failed', organizations: [], chosen: null, error: messageOf(error), failure: error })
    }

  }

  // "Open <org>" on an accepted invite: sign in to that organization
  // directly. When the organization list doesn't offer it (it should, once
  // accepted), fall back to the organization step.
  const handleOpenInvite = async invite => {
    let organizations

    try {
      organizations = await apiFormAuthProvider.get(`${token}/organization`)
    } catch (error) {
      if (onSpent(error)) return
      throw error
    }
    const match = Array.isArray(organizations)
      ? organizations.find(o => !o.joinable && String(o.organizationID) === String(invite.organizationID))
      : null

    if (match) {
      await handleSelect(match, organizations)
    } else {
      await loadOrganizations()
    }
  }

  if (spent) {
    return <SignInLinkSpent reason={spent} />
  }

  if (state.step === 'invites') {
    return <InviteConfirmation invites={state.invites} onDecide={handleDecide} onContinue={loadOrganizations} onOpen={handleOpenInvite} />
  }

  if (state.step === 'failed') {
    return (
      <AuthPage labelledBy="org-title">
        <AuthHead id="org-title" title="We couldn't finish signing you in" mark={<AuthMark tone="bad">!</AuthMark>} />
        <p className="m-0">
          Your sign-in worked, but we couldn't load your organizations. The link may have expired: sign-in links last 15 minutes.
        </p>
        {state.error &&
          <AuthNotice tone="bad" role="alert">
            <p>Details: {state.error}</p>
          </AuthNotice>}
        <ErrorReference error={state.failure} />
        <Link className="btn sw-btn-primary sa-block" to="/auth/email">Start again</Link>
      </AuthPage>
    )
  }

  if (state.step === 'join') {
    const any = state.organizations.length > 0
    return (
      <AuthPage labelledBy="org-title">
        <AuthHead
          id="org-title"
          title={any ? 'Choose your organization' : 'Join your organization'}
          lede={any
            ? 'Your sign-in worked. Choose the EducationXR organization to join. You join only the one you choose.'
            : 'Your sign-in worked. Enter the join code your organization gave you to join it.'}
        />
        <RemovedMembershipNotice entries={state.notice} />
        {state.error && state.removed &&
          <AuthNotice tone="warn" role="alert"><p>{state.removed}</p></AuthNotice>}
        {state.error && !state.removed &&
          <AuthNotice tone="bad" role="alert">
            <p>We couldn't add you to {(state.chosen && state.chosen.name) || 'that organization'}. Try again, or choose another.</p>
            <ErrorReference error={state.failure} />
          </AuthNotice>}
        {any &&
          <OrganizationChooser organizations={state.organizations} labelledBy="org-title" onSelect={organization => handleSelect(organization)} />}
        {any && state.code && <AuthDivider />}
        {state.code && <JoinCodeEntry onJoin={handleJoinCode} startOpen={!any} />}
        {!any && !state.code &&
          <AuthNotice tone="bad" role="alert"><p>There's no organization for you to join here yet. Ask your organization's administrator.</p></AuthNotice>}
      </AuthPage>
    )
  }

  const choosing = state.step === 'choose'
  const several = state.organizations.length > 1

  return (
    <AuthPage labelledBy="org-title">
      <AuthHead
        id="org-title"
        title={choosing && several ? 'Choose an organization' : 'Signing you in'}
        lede={choosing && several
          ? 'Your account belongs to more than one EducationXR organization. Choose the one to use now. You can switch organizations at any time after signing in.'
          : undefined}
      />

      {state.step === 'loading' && <AuthLoading>Finding your organizations…</AuthLoading>}

      {state.step === 'selecting' && state.chosen &&
        <AuthLoading>Signing you in to {state.chosen.name}…</AuthLoading>}

      {choosing && <RemovedMembershipNotice entries={state.notice} />}

      {state.error && state.removed &&
        <AuthNotice tone="warn" role="alert"><p>{state.removed}</p></AuthNotice>}
      {state.error && !state.removed &&
        <AuthNotice tone="bad" role="alert">
          <p>We couldn't sign you in to {(state.chosen && state.chosen.name) || 'that organization'}. Try again, or choose another.</p>
          <ErrorReference error={state.failure} />
        </AuthNotice>}

      {choosing &&
        <OrganizationChooser
          organizations={state.organizations}
          flagKey={state.last}
          labelledBy="org-title"
          onSelect={organization => handleSelect(organization)}
        />}

      {choosing &&
        <div className="sa-chooser-foot">
          <Link className="btn sw-btn" to="/auth/email">Cancel</Link>
        </div>}
    </AuthPage>
  )
}
